import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  fetchBuyerInfrastructureDisputeDetail,
  fetchBuyerInfrastructureDisputes,
  fetchSellerInfrastructureDisputeDetail,
  fetchSellerInfrastructureDisputes,
  postBuyerInfrastructureDispute,
  postInfrastructureDisputeEvidence,
  postInfrastructureDisputeMessage,
  postInfrastructureDisputeWithdraw,
  postSellerInfrastructureDispute,
} from '../../services/infrastructureLeasingListing.service';
import { resolvePublicFileUrl } from '../../constants/api-urls';

const CATEGORIES = [
  { value: 'lease_delivery_asset_availability', label: 'Lease delivery / asset availability' },
  { value: 'early_termination_disagreement', label: 'Early termination disagreement' },
  { value: 'mutual_termination_issue', label: 'Mutual termination issue' },
  { value: 'payment_mismatch_failure', label: 'Payment mismatch / failure' },
  { value: 'refund_dispute', label: 'Refund dispute' },
  { value: 'seller_payout_dispute', label: 'Seller payout dispute' },
  { value: 'penalty_dispute', label: 'Penalty dispute' },
  { value: 'invoice_tax_document_dispute', label: 'Invoice / tax document dispute' },
  { value: 'asset_return_damage_claim', label: 'Asset return / damage claim' },
  { value: 'reconciliation_exception', label: 'Reconciliation exception' },
  { value: 'system_anomaly', label: 'System anomaly' },
  { value: 'other', label: 'Other' },
];

const LINKED_TYPES = [
  { value: 'none', label: 'None (general)' },
  { value: 'infrastructure_payment_order', label: 'Payment order' },
  { value: 'infrastructure_settlement_tracking', label: 'Settlement' },
  { value: 'infrastructure_ledger_entry', label: 'Ledger entry' },
  { value: 'infrastructure_commercial_document', label: 'Commercial document' },
  { value: 'infrastructure_lease_termination_financials', label: 'Termination financials' },
  { value: 'infrastructure_listing_agreement', label: 'Agreement' },
  { value: 'infrastructure_mutual_lease_termination', label: 'Mutual termination record' },
  { value: 'inventory_allocation', label: 'Inventory allocation' },
  { value: 'other', label: 'Other linked record' },
];

function statusBadgeClass(st) {
  const s = String(st || '').toLowerCase();
  if (s === 'open' || s === 'awaiting_counterparty_response') return 'bg-warning text-dark';
  if (s === 'under_review' || s === 'admin_review') return 'bg-info text-dark';
  if (s === 'resolved') return 'bg-success';
  if (s === 'rejected' || s === 'withdrawn') return 'bg-secondary';
  if (s === 'escalated') return 'bg-danger';
  if (s === 'closed') return 'bg-dark';
  return 'bg-secondary';
}

export default function InfrastructureLeasingDisputePanel({
  variant,
  listingIdNum,
  enquiryIdNum,
  disputeSummary,
  onRefresh,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [raiseBusy, setRaiseBusy] = useState(false);
  const [msgBody, setMsgBody] = useState('');
  const [replyBusy, setReplyBusy] = useState(false);

  const [form, setForm] = useState({
    disputeCategory: 'other',
    linkedEntityType: 'none',
    linkedEntityId: '',
    priority: 'medium',
    claimAmount: '',
    currency: '',
    subject: '',
    description: '',
  });

  const loadList = useCallback(async () => {
    if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) return;
    setLoading(true);
    try {
      const res =
        variant === 'buyer'
          ? await fetchBuyerInfrastructureDisputes(listingIdNum, enquiryIdNum)
          : await fetchSellerInfrastructureDisputes(listingIdNum, enquiryIdNum);
      if (res?.success && res.data?.items) setItems(res.data.items);
      else setItems([]);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not load disputes.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [variant, listingIdNum, enquiryIdNum]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const openDetail = async (disputeId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res =
        variant === 'buyer'
          ? await fetchBuyerInfrastructureDisputeDetail(listingIdNum, enquiryIdNum, disputeId)
          : await fetchSellerInfrastructureDisputeDetail(listingIdNum, enquiryIdNum, disputeId);
      if (res?.success && res.data) setDetail(res.data);
      else toast.error(res?.message || 'Could not load dispute.');
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not load dispute.');
    } finally {
      setDetailLoading(false);
    }
  };

  const submitRaise = async () => {
    if (!form.subject.trim() || !form.description.trim()) {
      toast.warning('Subject and description are required.');
      return;
    }
    const body = {
      disputeCategory: form.disputeCategory,
      linkedEntityType: form.linkedEntityType,
      priority: form.priority,
      subject: form.subject.trim(),
      description: form.description.trim(),
    };
    if (form.linkedEntityType !== 'none' && form.linkedEntityId) {
      body.linkedEntityId = parseInt(String(form.linkedEntityId), 10);
    }
    if (form.claimAmount) body.claimAmount = parseFloat(String(form.claimAmount));
    if (form.currency.trim()) body.currency = form.currency.trim();

    setRaiseBusy(true);
    try {
      const res =
        variant === 'buyer'
          ? await postBuyerInfrastructureDispute(listingIdNum, enquiryIdNum, body)
          : await postSellerInfrastructureDispute(listingIdNum, enquiryIdNum, body);
      if (res?.success) {
        toast.success('Dispute raised.');
        setRaiseOpen(false);
        await loadList();
        if (typeof onRefresh === 'function') await onRefresh();
      } else toast.error(res?.message || 'Could not raise dispute.');
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not raise dispute.');
    } finally {
      setRaiseBusy(false);
    }
  };

  const submitReply = async () => {
    const id = detail?.dispute?.disputeId;
    if (!id || !msgBody.trim()) return;
    setReplyBusy(true);
    try {
      const res = await postInfrastructureDisputeMessage(id, { message: msgBody.trim() });
      if (res?.success) {
        toast.success('Message sent.');
        setMsgBody('');
        await openDetail(id);
        await loadList();
      } else toast.error(res?.message || 'Failed.');
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed.');
    } finally {
      setReplyBusy(false);
    }
  };

  const submitWithdraw = async () => {
    const id = detail?.dispute?.disputeId;
    if (!id) return;
    if (!window.confirm('Withdraw this dispute?')) return;
    try {
      const res = await postInfrastructureDisputeWithdraw(id);
      if (res?.success) {
        toast.success('Withdrawn.');
        setDetailOpen(false);
        await loadList();
        if (typeof onRefresh === 'function') await onRefresh();
      } else toast.error(res?.message || 'Could not withdraw.');
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not withdraw.');
    }
  };

  const uploadEvidence = async (ev) => {
    const id = detail?.dispute?.disputeId;
    if (!id || !ev.target.files?.length) return;
    const file = ev.target.files[0];
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await postInfrastructureDisputeEvidence(id, fd);
      if (res?.success) {
        toast.success('Evidence uploaded.');
        await openDetail(id);
      } else toast.error(res?.message || 'Upload failed.');
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Upload failed.');
    } finally {
      ev.target.value = '';
    }
  };

  const summary = disputeSummary || {
    openDisputeCount: 0,
    hasCriticalDispute: false,
    latestDisputeStatus: null,
  };

  return (
    <div className="card border shadow-sm mb-4 ile-dispute-panel d-print-none">
      <div className="card-header bg-white fw-semibold py-2 d-flex flex-wrap justify-content-between align-items-center gap-2">
        <span>Dispute &amp; support</span>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setRaiseOpen(true)}>
          Raise dispute
        </button>
      </div>
      <div className="card-body py-3 small">
        <div className="row g-2 mb-3">
          <div className="col-md-4">
            <div className="text-muted">Open disputes</div>
            <div className="fw-semibold">{summary.openDisputeCount ?? 0}</div>
          </div>
          <div className="col-md-4">
            <div className="text-muted">Critical flag</div>
            <div className="fw-semibold">{summary.hasCriticalDispute ? 'Yes' : 'No'}</div>
          </div>
          <div className="col-md-4">
            <div className="text-muted">Latest status</div>
            <div>
              {summary.latestDisputeStatus ? (
                <span className={`badge ${statusBadgeClass(summary.latestDisputeStatus)}`}>
                  {summary.latestDisputeStatus}
                </span>
              ) : (
                '—'
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <p className="text-muted mb-0">Loading disputes…</p>
        ) : items.length === 0 ? (
          <p className="text-muted mb-0">No disputes filed for this enquiry.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-bordered mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Number</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Claim</th>
                  <th>Updated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.disputeId}>
                    <td className="text-break">{row.disputeNumber}</td>
                    <td>{row.disputeCategory}</td>
                    <td>
                      <span className={`badge ${statusBadgeClass(row.status)}`}>{row.status}</span>
                    </td>
                    <td>
                      {row.claimAmount != null ? `${row.claimAmount} ${row.currency || ''}` : '—'}
                    </td>
                    <td>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link btn-sm py-0"
                        onClick={() => void openDetail(row.disputeId)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {raiseOpen ? (
        <div className="modal show d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Raise dispute</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setRaiseOpen(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label">Category</label>
                    <select
                      className="form-select form-select-sm"
                      value={form.disputeCategory}
                      onChange={(e) => setForm((f) => ({ ...f, disputeCategory: e.target.value }))}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Priority</label>
                    <select
                      className="form-select form-select-sm"
                      value={form.priority}
                      onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Linked record type</label>
                    <select
                      className="form-select form-select-sm"
                      value={form.linkedEntityType}
                      onChange={(e) => setForm((f) => ({ ...f, linkedEntityType: e.target.value }))}
                    >
                      {LINKED_TYPES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Linked record ID</label>
                    <input
                      className="form-control form-control-sm"
                      disabled={form.linkedEntityType === 'none'}
                      value={form.linkedEntityId}
                      onChange={(e) => setForm((f) => ({ ...f, linkedEntityId: e.target.value }))}
                      placeholder="Numeric id when linked"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Claim amount (optional)</label>
                    <input
                      className="form-control form-control-sm"
                      value={form.claimAmount}
                      onChange={(e) => setForm((f) => ({ ...f, claimAmount: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Currency (optional)</label>
                    <input
                      className="form-control form-control-sm"
                      value={form.currency}
                      onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                      placeholder="e.g. INR"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Subject</label>
                    <input
                      className="form-control form-control-sm"
                      value={form.subject}
                      onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={4}
                      value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setRaiseOpen(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary btn-sm" disabled={raiseBusy} onClick={() => void submitRaise()}>
                  {raiseBusy ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {detailOpen ? (
        <div className="modal show d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {detail?.dispute?.disputeNumber ? `Dispute ${detail.dispute.disputeNumber}` : 'Dispute'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => {
                    setDetailOpen(false);
                    setDetail(null);
                  }}
                />
              </div>
              <div className="modal-body">
                {detailLoading ? (
                  <p className="text-muted">Loading…</p>
                ) : detail?.dispute ? (
                  <>
                    <div className="mb-3">
                      <span className={`badge ${statusBadgeClass(detail.dispute.status)}`}>{detail.dispute.status}</span>{' '}
                      <span className="text-muted">{detail.dispute.disputeCategory}</span>
                    </div>
                    <p className="fw-semibold mb-1">{detail.dispute.subject}</p>
                    <p className="small text-muted text-pre-wrap">{detail.dispute.description}</p>

                    <h6 className="mt-3">Messages</h6>
                    <ul className="list-unstyled small border rounded p-2 bg-light">
                      {(detail.messages || []).map((m) => (
                        <li key={m.messageId} className="mb-2 pb-2 border-bottom">
                          <div className="text-muted">
                            {m.senderRole} · {m.createdAt ? new Date(m.createdAt).toLocaleString() : ''}
                          </div>
                          <div>{m.message}</div>
                        </li>
                      ))}
                    </ul>
                    <div className="mb-3">
                      <label className="form-label small">Reply</label>
                      <textarea
                        className="form-control form-control-sm"
                        rows={2}
                        value={msgBody}
                        onChange={(e) => setMsgBody(e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-primary mt-1"
                        disabled={replyBusy}
                        onClick={() => void submitReply()}
                      >
                        Send message
                      </button>
                    </div>

                    <h6>Evidence</h6>
                    <ul className="small">
                      {(detail.evidence || []).map((ev) => (
                        <li key={ev.evidenceId}>
                          <a href={resolvePublicFileUrl(ev.url || ev.filePath)} target="_blank" rel="noopener noreferrer">
                            {ev.fileName}
                          </a>
                        </li>
                      ))}
                    </ul>
                    <input type="file" className="form-control form-control-sm" onChange={(e) => void uploadEvidence(e)} />

                    <div className="mt-3 d-flex gap-2 flex-wrap">
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => void submitWithdraw()}>
                        Withdraw dispute
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="text-muted">No data.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
